import { useCallback } from 'react';
import type { ActionHandlerType, OnActionArgs } from '../../../../context/ActionsProvider/context';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { createWalkthroughInstanceEventBus } from '../../../../types/walkthroughInstanceEventBus';
import { SHOULD_NOT_PROPAGATE_UP } from '../../const';
import type { CONTROL_WALKTHROUGH_METHODS } from './schema/controlWalkthrough';

type ControlWalkthroughParams = {
  walkthroughId?: string;
  method: keyof typeof CONTROL_WALKTHROUGH_METHODS;
};

export const useControlWalkthrough = () => {
  const interfaceId = useGlobalAppStateStore().hooks.record.useId();

  const controlWalkthrough: ActionHandlerType = useCallback(
    ({ action }: OnActionArgs) => {
      const { walkthroughId, method } = action.payload as ControlWalkthroughParams;
      const walkthroughInstanceEventBus = createWalkthroughInstanceEventBus(interfaceId);

      switch (method) {
        case 'startWalkthrough':
          if (walkthroughId) {
            walkthroughInstanceEventBus.publish('onControlWalkthrough', {
              method: 'start',
              walkthroughId,
            });
          } else {
            console.error('walkthroughId is required for startWalkthrough method');
          }
          break;
        case 'stopWalkthrough':
          walkthroughInstanceEventBus.publish('onControlWalkthrough', { method: 'stop' });
          break;
        case 'ackPreStart':
          walkthroughInstanceEventBus.publish('onWalkthroughPreStartAck', { handling: true });
          break;
        case 'declinePreStart':
          walkthroughInstanceEventBus.publish('onWalkthroughPreStartAck', { handling: false });
          break;
        case 'donePreStart':
          walkthroughInstanceEventBus.publish(
            'onWalkthroughPreStartDone',
            {} as Record<string, never>,
          );
          break;
        case 'ackPreEnd':
          walkthroughInstanceEventBus.publish('onWalkthroughPreEndAck', { handling: true });
          break;
        case 'declinePreEnd':
          walkthroughInstanceEventBus.publish('onWalkthroughPreEndAck', { handling: false });
          break;
        case 'donePreEnd':
          walkthroughInstanceEventBus.publish(
            'onWalkthroughPreEndDone',
            {} as Record<string, never>,
          );
          break;
        default:
          console.error(`Unknown method: ${method as string}`);
      }

      return Promise.resolve(SHOULD_NOT_PROPAGATE_UP);
    },
    [interfaceId],
  );

  return { controlWalkthrough };
};
